import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import "./style.css";

const CustomSearchDropdown = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Select...',
    name = '',
    className = '',
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const controlRef = useRef(null);
    const menuPortalRef = useRef(null); // 👈 new ref for portal container
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

    const selectedOption = options.find(opt => opt.value === value);
    const displayLabel = selectedOption?.label || '';

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (option) => {
        console.log("🟢 CustomSearchDropdown selected:", option);
        onChange({
            target: {
                name: name,
                value: option.value,
            },
        });
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange({
            target: {
                name: name,
                value: '',
            },
        });
        setSearchTerm('');
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            if (!isOpen) {
                const rect = controlRef.current?.getBoundingClientRect();
                if (rect) {
                    setMenuPosition({
                        top: rect.bottom + window.scrollY,
                        left: rect.left + window.scrollX,
                        width: rect.width,
                    });
                }
                setSearchTerm('');
            }
            setIsOpen(!isOpen);
        }
    };

    // 🔥 Fixed outside click handler – ignore clicks inside portal menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // If click is inside the portal menu, do nothing
            if (menuPortalRef.current && menuPortalRef.current.contains(event.target)) {
                return;
            }
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const updatePosition = () => {
            if (isOpen && controlRef.current) {
                const rect = controlRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                });
            }
        };
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    return (
        <div className={`custom-dropdown-wrapper ${className}`} ref={wrapperRef}>
            <div
                ref={controlRef}
                className={`custom-dropdown-control ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={toggleDropdown}
            >
                <span className="custom-dropdown-value">
                    {displayLabel || placeholder}
                </span>
                <div className="custom-dropdown-actions">
                    {value && !disabled && (
                        <span className="custom-dropdown-clear" onClick={handleClear}>
                            ✕
                        </span>
                    )}
                    <span className="custom-dropdown-arrow">▼</span>
                </div>
            </div>

            {isOpen && !disabled && createPortal(
                <div
                    ref={menuPortalRef} // 👈 assign ref here
                    className="custom-dropdown-menu"
                    style={{
                        position: 'absolute',
                        top: menuPosition.top,
                        left: menuPosition.left,
                        width: menuPosition.width,
                        zIndex: 99999,
                        maxHeight: '220px',
                        overflow: 'auto',
                        background: '#fff',
                        border: '1px solid #ced4da',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                >
                    <div className="custom-dropdown-search-wrap">
                        <input
                            ref={inputRef}
                            type="text"
                            className="custom-dropdown-search"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <ul className="custom-dropdown-list">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(opt => (
                                <li
                                    key={opt.value}
                                    className={opt.value === value ? 'selected' : ''}
                                    onClick={() => {
                                        console.log('🔵 List item clicked:', opt);
                                        handleSelect(opt);
                                    }}
                                >
                                    {opt.label}
                                </li>
                            ))
                        ) : (
                            <li className="no-options">No results found</li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CustomSearchDropdown;